import React from 'react';
import {
  radioSchema,
  radioUI,
  titleUI,
} from 'platform/forms-system/src/js/web-component-patterns';

import { confirmEvidenceRemovalPage } from '../../../content/confirmEvidenceRemovalPage';
import {
  anyEvidence,
  buildItemGroups,
} from '../../../content/evidenceRemovalContent';

export const WhichOptionShouldIChoose = () => (
  <va-accordion>
    <va-accordion-item
      header="Which option should I choose?"
      level="4"
      bordered
    >
      <p className="vads-u-margin-top--0">
        <strong>If you upload your medical records,</strong> we can review your
        claim faster. You can upload .pdf, .jpeg, or .png files.
      </p>
      <p>
        <strong>If you have VA request your records,</strong> it could take us
        longer to decide your claim. In this case, we’ll ask you to give us
        information about the provider or hospital that treated you. And it may
        take some time for us to get them.
      </p>
      <p>
        We’ll try at least 2 times to get your records from the provider or
        hospital. If we can’t get them, we’ll let you know and tell you if
        there’s anything you need to do.
      </p>
      <va-link
        href="https://www.va.gov/find-forms/about-form-21-4142/"
        external
        text="Learn more about the Authorization to disclose medical records to VA (VA Form 21-4142)"
      />
    </va-accordion-item>
  </va-accordion>
);

/**
 * Destructive modal for /supporting-evidence/other-providers-sharing-options
 * Displays when the user switches how VA should get their private records
 * after already providing evidence under the other option:
 *  - switching to "VA requests them" deletes the files they uploaded
 *  - switching to "I'll upload them" removes the providers they listed
 */
const uploadsAbandoned = formData =>
  formData?.['view:privateMedicalRecordsAccess'] === 'vaRequest'
    ? formData?.privateMedicalRecordsUploads || []
    : [];

const providersAbandoned = formData =>
  formData?.['view:privateMedicalRecordsAccess'] === 'selfUpload'
    ? formData?.providerFacility || []
    : [];

const AccessChangeRemovalPage = confirmEvidenceRemovalPage({
  shouldConfirm: formData =>
    anyEvidence(uploadsAbandoned(formData), providersAbandoned(formData)),
  // Only one of these can be non-empty, so only one group ever shows.
  getItemGroups: formData =>
    buildItemGroups([
      {
        items: uploadsAbandoned(formData),
        kind: 'file',
        phrase: 'delete these files',
      },
      {
        items: providersAbandoned(formData),
        kind: 'provider',
        phrase: 'remove the information you shared about these providers',
      },
    ]),
  // Only the abandoned option's evidence goes; the new option's stays.
  removeItems: formData => {
    const updated = { ...formData };
    if (uploadsAbandoned(formData).length) {
      delete updated.privateMedicalRecordsUploads;
    }
    if (providersAbandoned(formData).length) {
      delete updated.providerFacility;
    }
    return updated;
  },
  // Dismissing puts the radio back to the option whose evidence still exists.
  revertSelection: formData => ({
    ...formData,
    'view:privateMedicalRecordsAccess': uploadsAbandoned(formData).length
      ? 'selfUpload'
      : 'vaRequest',
  }),
  modalTitle: 'Change your medical records?',
  // The lead-in names the option being given up, so it differs per direction.
  modalIntro: formData =>
    uploadsAbandoned(formData).length ? (
      <p>You can choose to remove the medical records you’ve uploaded.</p>
    ) : (
      <p>
        You can choose not to have VA request medical records from other
        healthcare providers.
      </p>
    ),
  // Files are deleted; provider information is removed.
  primaryButtonText: formData =>
    uploadsAbandoned(formData).length
      ? 'Change and delete'
      : 'Change and remove',
  secondaryButtonText: 'Cancel change',
  alertText: formData =>
    uploadsAbandoned(formData).length
      ? 'We’ve deleted the medical records you uploaded.'
      : 'We’ve removed information about your healthcare providers.',
});

export default {
  CustomPage: AccessChangeRemovalPage,
  CustomPageReview: null,
  uiSchema: {
    ...titleUI('Options for sharing records from other healthcare providers'),
    'view:privateMedicalRecordsAccess': radioUI({
      title: 'How would you like us to access these records?',
      labels: {
        selfUpload: 'I\u2019ll upload the records myself',
        vaRequest:
          'I want VA to request my medical records from other healthcare providers',
      },
    }),
    'view:privateMedicalRecordsAccessInfo': {
      'ui:description': () => (
        <>
          <p>
            <strong>Note:</strong> If you want us to request your records, we’ll
            ask for your permission on the next step to get your medical records
            from other healthcare providers.
          </p>
          <WhichOptionShouldIChoose />
        </>
      ),
    },
  },
  schema: {
    type: 'object',
    required: ['view:privateMedicalRecordsAccess'],
    properties: {
      'view:privateMedicalRecordsAccess': radioSchema([
        'selfUpload',
        'vaRequest',
      ]),
      'view:privateMedicalRecordsAccessInfo': {
        type: 'object',
        properties: {},
      },
    },
  },
};
